import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageWrapper from "../pageWrapper";

// CSS
import "./employeeEditPage.css";
import "../0PageStyles.css";

// Components
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import PageLoader from "../../Components/Other Components/pageLoader";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";
import RolePicker from "../../Components/Role Components/rolePicker";

// Functions and hooks
import {
  GetEmployeeData,
  EditEmployeeData,
  RemoveEmployee,
} from "../../BackendAccess/EmployeeHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { GetStorageLink } from "../../BackendAccess/FrontendHelpers";
import { GetRoleData } from "../../BackendAccess/RoleHooks";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import useFormState from "../../hooks/useFormStates";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

export default function EmployeeViewPage() {
  const { formData, setField, loadInitialData, hasChanged } = useFormState({
    name: "",
    employeeID: "",
    username: "",
    email: "",
    role: "",
    roleID: "",
    image: null,
  });

  const [allowedWarehouses, setAllowedWarehouses] = useState([]);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();

  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();

  const {
    state: { id: EmployeeID },
  } = useLocation();

  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const showConfirmationPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t(
              "Are you sure you want to remove this user from your employee list?",
              { ns: "employees" }
            )}
            <br />
            {t(
              "For the user to rejoin your warehouse management list you would have to send them a new invite and they would have to accept it.",
              { ns: "employees" }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => handleRemove(key)}
        onCancel={() => handleCancelRemove(key)}
      />,
      key
    );
  };

  useEffect(() => {
    fetchEmployeeData();
  }, [EmployeeID]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetEmployeeData(EmployeeID);

      if (error) {
        console.log("ERRORRRRRRR1", error);

        error.isPageError = true;
        setError(error);
      } else {
        setEmployeeData(data);
        await GetAllowedWarehouses(data.RoleID);
      }
    } catch (err) {
      console.log("ERRORRRRRRR2");
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const GetAllowedWarehouses = async (roleID) => {
    if (!roleID) {
      setAllowedWarehouses([]);
      return;
    }
    try {
      const [data, error] = await fetchWithTimeout(() => GetRoleData(roleID));
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setAllowedWarehouses(data.AllowedWarehouses);
      }
    } catch (err) {
      throw new Error(
        t("Error while getting allowed warehouses!", { ns: "employees" })
      );
    }
  };

  const setEmployeeData = (employee) => {
    const imagePath = employee.ImagePath
      ? GetStorageLink() + employee.ImagePath
      : null;
    loadInitialData({
      employeeID: employee.UserID,
      email: employee.Email,
      username: employee.Username,
      name: employee.FirstName + " " + employee.LastName,
      role: employee.Role || "none",
      roleID: employee.RoleID,
      image: imagePath,
    });
  };

  const handleRoleChange = (newRoleArray) => {
    setField("roleID", newRoleArray[0]);
  };

  const handleCancelClick = () => {
    navigate(-1);
  };

  const handleCancelRemove = (key) => {
    removePopup(key);
  };

  const handleRemove = async (key) => {
    setDeleteBtnDisabled(true);
    showNotification(t("Removing employee...", { ns: "employees" }), "info");
    try {
      const [data, error] = await fetchWithTimeout(() =>
        RemoveEmployee(formData.employeeID)
      );
      if (error) {
        error.isPageError = true;
        setError(error);
        setDeleteBtnDisabled(false);
      } else {
        showNotification(
          t("Employee removed successfully!", { ns: "employees" }),
          "success"
        );
        navigate(-1);
      }
    } catch (err) {
      setDeleteBtnDisabled(false);
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      removePopup(key);
    }
  };

  const handleApplyChanges = async () => {
    showNotification(
      t("Updating employee role...", { ns: "employees" }),
      "info"
    );
    try {
      const [data, error] = await fetchWithTimeout(() =>
        EditEmployeeData(formData.employeeID, formData.roleID)
      );
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        showNotification(
          t("Employee role updated successfully!", { ns: "employees" }),
          "success"
        );
        navigate(-1);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    }
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Viewing an employee", { ns: "tutorial" }),
      message: t(
        "This page shows one employee's details and lets you change their role or remove them from your company.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "employee-edit-details",
      title: t("Employee details", { ns: "tutorial" }),
      message: t(
        "The employee's ID, email, username, and name. These are fixed and can't be changed here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "employee-edit-role",
      title: t("Role", { ns: "tutorial" }),
      message: t(
        "Change what this employee is allowed to do by assigning them a different role.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "employee-edit-warehouses",
      title: t("Warehouse access", { ns: "tutorial" }),
      message: t(
        "Shows which warehouses this employee can access, based on their current role.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "employee-edit-apply-btn",
      title: t("Save your changes", { ns: "tutorial" }),
      message: t(
        "Once you're happy with your changes, click here to save them. This button only lights up once something has actually changed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "employee-edit-remove-btn",
      title: t("Remove employee", { ns: "tutorial" }),
      message: t(
        "Removes this person from your employee list. They'd need a new invite to rejoin. This cannot be undone.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="employeeEdit" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content pt-10">
          <div
            className={
              "container-empty mb-2 w-90 " +
              (isNarrow ? "vertical" : "horizontal")
            }
            data-tutorial-id="employee-edit-details"
          >
            <div className="user-settings-image-container">
              <InputField
                type={"file"}
                previewImage={formData.image}
                label={t("No image.")}
                readOnly={true}
              />
            </div>
            <div className="container-empty employee-settings-fields vertical">
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                readOnly={true}
                label={t("ID") + ":"}
                value={formData.employeeID}
              />
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                readOnly={true}
                label={t("Email") + ":"}
                value={formData.email}
              />
            </div>
            <div className="container-empty employee-settings-fields vertical">
              <InputField
                className="mb-2 "
                labelPosition={"top-left"}
                label={t("Username") + ":"}
                value={formData.username}
                readOnly={true}
              />
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                label={t("Name") + ":"}
                value={formData.name}
                readOnly={true}
              />
            </div>
          </div>
          <div data-tutorial-id="employee-edit-role">
            <RolePicker
              className={"w-90 mt-5"}
              singleSelect={true}
              initSelectedRoles={[formData.roleID]}
              onChange={handleRoleChange}
            />
          </div>
          <div data-tutorial-id="employee-edit-warehouses">
            <WarehousePicker
              className={"w-90 mt-5"}
              selectable={false}
              initWarehouses={allowedWarehouses}
            />
          </div>

          <div className="btn-container w-90 mt-3 mb-4">
            <Button
              className={isNarrow ? "" : "ml-3"}
              btnStyle={"btn-positive"}
              icon={"apply"}
              onClick={handleApplyChanges}
              disabled={!hasChanged}
              tutorialId="employee-edit-apply-btn"
            >
              {t("Apply Changes")}
            </Button>
            <Button
              className={isNarrow ? "" : "ml-3"}
              onClick={handleCancelClick}
            >
              {t("Cancel")}
            </Button>
            <Button
              className={isNarrow ? "mb-2" : "ml-3"}
              btnStyle={"btn-negative"}
              icon={"delete"}
              onClick={showConfirmationPopup}
              disabled={deleteBtnDisabled}
              tutorialId="employee-edit-remove-btn"
            >
              {t("Remove employee")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
